import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Nav, NavItem, NavLink, Row } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { getQualityControl, recordQualityCheck, saveQcRule } from './api';
import './devir-teslim.css';

// Operasyonlar > Operasyon İzleme > Kalite Kontrol (row 53): the quality manual's controls by stock age. Each rule is
// first due after N days in stock and then every M days; due controls per car, control records (append-only), rules.
// Overdue controls also appear in İstisnalar / SLA. Role group 'Operasyon İzleme - Kalite Kontrol' (026).
const ROLE_GROUP = 36;
const STATE = { GECIKTI: ['Gecikti', 'danger'], YAKLASIYOR: ['2 gün içinde', 'warning'], OK: ['Planlı', 'secondary'], TAMAM: ['Yapıldı', 'success'] };

const CheckForm = ({ d, onSaved, onCancel }) => {
    const [result, setResult] = useState('UYGUN');
    const [note, setNote] = useState('');
    const save = () => recordQualityCheck({ chassis_no: d.chassis_no, rule_code: d.rule_code, result, note })
        .then(() => { toast.success('Kontrol kaydedildi'); onSaved(); })
        .catch((e) => toast.error(e?.response?.data?.error || 'Kaydedilemedi'));
    return (
        <div className="dt-draft">
            <div className="dt-muted mb-1">{d.checklist}</div>
            <Row>
                <Col md="3"><Input type="select" value={result} onChange={(e) => setResult(e.target.value)}>
                    <option value="UYGUN">Uygun</option><option value="UYGUN_DEGIL">Uygun değil</option></Input></Col>
                <Col md="6"><Input value={note} placeholder={result === 'UYGUN_DEGIL' ? 'Açıklama (zorunlu): bulgu ve yapılan işlem' : 'Açıklama (ör. akü 12,6 V)'}
                    onChange={(e) => setNote(e.target.value)} /></Col>
                <Col md="3"><Button color="primary" className="mr-1" onClick={save}>Kaydet</Button><Button color="secondary" onClick={onCancel}>Vazgeç</Button></Col>
            </Row>
        </div>
    );
};

const KaliteKontrol = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canWrite = hasRole('kalite_kontrol_write');
    const [tab, setTab] = useState('due');
    const [data, setData] = useState({ rules: [], due: [], checks: [] });
    const [state, setState] = useState('');
    const [vin, setVin] = useState('');
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => {
        getQualityControl().then(setData).catch((e) => toast.error(e?.response?.data?.error || 'Kalite kontrol planı alınamadı'));
    }, []);
    useEffect(load, [load]);
    const persist = (code, values) => saveQcRule(code, values)
        .then(() => { toast.success('Kaydedildi'); load(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });
    const count = (s) => data.due.filter((d) => d.state === s).length;
    const rows = data.due.filter((d) => (!state || d.state === state) && (!vin || d.chassis_no.includes(vin)));
    return (
        <div>
            <Breadcrumb parent="Operasyon İzleme" current="Kalite Kontrol" />
            <Row className="mb-2">
                {[['GECIKTI', 'Gecikmiş kontrol', 'danger'], ['YAKLASIYOR', '2 gün içinde yapılacak', 'warning'], ['OK', 'Planlı', 'primary']].map(([s, t, c]) => (
                    <Col md="4" key={s}><Card style={{ cursor: 'pointer' }} onClick={() => { setTab('due'); setState(s); }}><CardBody className="py-3">
                        <div className="dt-muted">{t}</div><h3 className={`mb-0 text-${count(s) || s === 'OK' ? c : 'success'}`}>{count(s)}</h3>
                    </CardBody></Card></Col>
                ))}
            </Row>
            <Nav tabs className="border-tab nav-primary ci-tabs">
                {[['due', 'Yapılacak kontroller'], ['checks', 'Kontrol kayıtları'], ['rules', 'Kurallar (stok yaşı)']].map(([k, t]) => (
                    <NavItem key={k}><NavLink className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{t}</NavLink></NavItem>
                ))}
            </Nav>
            {tab === 'due' && (
                <Card><CardBody>
                    <div className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
                        <Input bsSize="sm" style={{ maxWidth: 240 }} placeholder="Şase ara" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} />
                        <Input bsSize="sm" type="select" style={{ maxWidth: 200 }} value={state} onChange={(e) => setState(e.target.value)}>
                            <option value="">Tüm durumlar</option>
                            {Object.entries(STATE).map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}
                        </Input>
                        <span className="dt-muted">Stok yaşı, aracın sahaya girişinden (gemi tahliyesi / depo girişi) itibaren.</span>
                    </div>
                    <table className="ct-table">
                        <thead><tr><th>Durum</th><th>VIN</th><th>Depo</th><th>Stok yaşı</th><th>Kontrol</th><th>Vade</th><th>Son kontrol</th><th></th></tr></thead>
                        <tbody>{rows.map((d) => (
                            <React.Fragment key={`${d.chassis_no}-${d.rule_code}`}>
                                <tr>
                                    <td><Badge color={STATE[d.state][1]}>{STATE[d.state][0]}</Badge></td>
                                    <td><b>{d.chassis_no}</b><div className="dt-muted">{d.brand}</div></td>
                                    <td>{d.warehouse}<div className="dt-muted">{d.zone}</div></td>
                                    <td>{d.stock_age_days.toFixed(1)} gün</td>
                                    <td>{d.rule_name}</td>
                                    <td className={d.state === 'GECIKTI' ? 'ci-age-late' : ''}>{d.due_at ? moment(d.due_at).format('DD.MM.YYYY HH:mm') : '—'}</td>
                                    <td>{d.last_checked_at ? `${moment(d.last_checked_at).format('DD.MM.YYYY HH:mm')} · ${d.last_result === 'UYGUN' ? 'uygun' : 'uygun değil'}` : '—'}</td>
                                    <td>{canWrite && d.state !== 'TAMAM' && <Button size="sm" color="primary" outline onClick={() => setEditing(`${d.chassis_no}-${d.rule_code}`)}>Kontrol kaydet</Button>}</td>
                                </tr>
                                {editing === `${d.chassis_no}-${d.rule_code}` && (
                                    <tr><td colSpan="8"><CheckForm d={d} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /></td></tr>
                                )}
                            </React.Fragment>
                        ))}</tbody>
                    </table>
                    {rows.length === 0 && <div className="dt-muted p-2">Kayıt yok.</div>}
                </CardBody></Card>
            )}
            {tab === 'checks' && (
                <Card><CardBody>
                    <table className="ct-table">
                        <thead><tr><th>Zaman</th><th>VIN</th><th>Kontrol</th><th>Sonuç</th><th>Açıklama</th><th>Kontrol eden</th><th>Depo</th></tr></thead>
                        <tbody>{data.checks.map((c, i) => (
                            <tr key={i}><td>{moment(c.checked_at).format('DD.MM.YYYY HH:mm')}</td><td><b>{c.chassis_no}</b></td><td>{c.rule_name}</td>
                                <td><Badge color={c.result === 'UYGUN' ? 'success' : 'danger'}>{c.result === 'UYGUN' ? 'Uygun' : 'Uygun değil'}</Badge></td>
                                <td>{c.note}</td><td>{c.checked_by}</td><td>{c.warehouse}</td></tr>
                        ))}</tbody>
                    </table>
                    {data.checks.length === 0 && <div className="dt-muted p-2">Kayıt yok.</div>}
                    <div className="dt-muted mt-2">Kontrol kayıtları değiştirilemez ve silinemez.</div>
                </CardBody></Card>
            )}
            {tab === 'rules' && (
                <Card><CardBody>
                    <div className="dt-muted mb-2">Kalite manueline göre: kontrol, araç stokta "ilk kontrol günü" kadar beklediğinde yapılır, sonra "tekrar (gün)" aralıkla (0 = bir kez). Değişiklikler loglanır.</div>
                    <DataGrid dataSource={data.rules} keyExpr="code" showBorders columnAutoWidth wordWrapEnabled
                        onRowUpdating={(e) => { e.cancel = persist(e.key, e.newData); }}
                        onRowInserting={(e) => { const { code, brand, ...values } = e.data; e.cancel = persist(code, values); }}>
                        <Editing mode="row" allowUpdating={canWrite} allowAdding={canWrite} />
                        <Column dataField="code" caption="Kod" allowEditing={false} width={140}
                            editCellRender={(c) => (c.row.isNewRow ? <Input maxLength={20} onChange={(e) => c.setValue(e.target.value.toUpperCase())} /> : c.value)} />
                        <Column dataField="name_tr" caption="Kontrol (TR)" />
                        <Column dataField="name_en" caption="Kontrol (EN)" />
                        <Column dataField="checklist" caption="Kontrol listesi" />
                        <Column dataField="first_due_days" caption="İlk kontrol (gün)" dataType="number" />
                        <Column dataField="repeat_days" caption="Tekrar (gün)" dataType="number" />
                        <Column dataField="is_active" caption="Aktif" dataType="boolean" />
                    </DataGrid>
                </CardBody></Card>
            )}
        </div>
    );
};

export default KaliteKontrol;
